import { useEffect, useState } from "react";
import { X, Smartphone, Apple, Download, Copy, RefreshCw, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tip } from "@/components/Tip";
import { fetchMobileLatest, mobileApkDownloadUrl, ApiError, type MobileAppLatest } from "@/lib/api";

interface MobilePanelProps {
  onClose: () => void;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** «Мобильное приложение» — dialog opened from the sidebar icon.
 *  Android: real APK download (account-cabinet channel, newest build).
 *  iOS: TestFlight link (owner fills it in) — placeholder until the
 *      iOS build exists. */
export function MobilePanel({ onClose }: MobilePanelProps) {
  const [latest, setLatest] = useState<MobileAppLatest | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      setLatest(await fetchMobileLatest());
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Не удалось загрузить.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyLink = async () => {
    if (!latest) return;
    try {
      await navigator.clipboard.writeText(mobileApkDownloadUrl(latest));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setErr("Не удалось скопировать ссылку.");
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div className="relative flex flex-col w-full max-w-md rounded-2xl border border-strongline bg-app shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-hairline">
          <h2 className="text-lg font-medium text-primary flex items-center gap-2">
            <Smartphone size={18} className="text-accent" /> Мобильное приложение
          </h2>
          <Tip label="Закрыть" side="bottom">
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-tertiary hover:text-primary hover:bg-hoverbg transition-colors"
            >
              <X size={18} />
            </button>
          </Tip>
        </div>

        <div className="px-5 py-5 space-y-3">
          {err && (
            <div className="text-xs px-3 py-2 rounded-lg bg-red-500/10 text-red-400">{err}</div>
          )}

          {/* Android */}
          <div className="rounded-xl border border-hairline bg-hoverbg/40 p-4 space-y-3">
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-md border border-hairline bg-app flex items-center justify-center text-accent">
                <Smartphone size={17} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-primary">Android</div>
                {latest ? (
                  <div className="text-[11px] text-tertiary truncate">
                    v{latest.version} (code {latest.versionCode}) · {(latest.size / 1024 / 1024).toFixed(1)} МБ ·{" "}
                    {fmtDate(latest.builtAt)}
                  </div>
                ) : (
                  <div className="text-[11px] text-tertiary">
                    {loading ? "Загрузка…" : "Самая свежая версия"}
                  </div>
                )}
              </div>
              <Tip label="Обновить" side="top">
                <button
                  onClick={() => void load()}
                  disabled={loading}
                  className="p-1.5 rounded-lg text-tertiary hover:text-primary hover:bg-hoverbg transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                </button>
              </Tip>
            </div>
            <div className="flex gap-2">
              <a
                href={latest ? mobileApkDownloadUrl(latest) : undefined}
                aria-disabled={!latest}
                className={cn(
                  "flex-1 h-10 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors",
                  latest
                    ? "bg-accent text-accent-contrast hover:opacity-90"
                    : "bg-hoverbg text-disabled cursor-not-allowed"
                )}
              >
                <Download size={15} /> Скачать APK
              </a>
              <Tip label="Копировать ссылку для телефона" side="top">
                <button
                  onClick={() => void copyLink()}
                  disabled={!latest}
                  className="h-10 px-3 rounded-lg text-sm flex items-center gap-1.5 border border-hairline bg-hoverbg text-primary hover:bg-hoverbg2 transition-colors disabled:opacity-50"
                >
                  {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                </button>
              </Tip>
            </div>
            <p className="text-[11px] text-tertiary/80">
              Ссылка действует 15 минут — откройте её в браузере телефона. При установке Android попросит
              разрешить «Неизвестные источники».
            </p>
          </div>

          {/* iOS */}
          <div className="rounded-xl border border-hairline bg-hoverbg/40 p-4 space-y-2.5">
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-md border border-hairline bg-app flex items-center justify-center text-accent">
                <Apple size={17} />
              </span>
              <div>
                <div className="text-sm font-medium text-primary">iOS</div>
                <div className="text-[11px] text-tertiary">Скоро — TestFlight</div>
              </div>
            </div>
            <div className="h-10 rounded-lg bg-hoverbg border border-hairline flex items-center justify-center text-sm text-tertiary">
              Ожидается iOS-сборка
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
